import React, { useState, useEffect, useCallback } from 'react';
import { incentiveAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Zap, ChevronDown, ChevronRight } from 'lucide-react';

const fmt = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export default function IncentivesTab() {
  const { user } = useAuth();
  const [pools, setPools]               = useState([]);
  const [distributions, setDistributions] = useState({});  // pool_id → []
  const [expandedPool, setExpandedPool] = useState(null);
  const [addingPool, setAddingPool]     = useState(false);
  const [distributing, setDistributing] = useState(null);
  const [saving, setSaving]             = useState(false);

  const today   = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    name: '', total_amount: '', distribution_type: 'hours',
    period_start: weekAgo, period_end: today,
  });

  const loadPools = useCallback(async () => {
    try {
      const r = await incentiveAPI.getPools();
      setPools(r.data?.pools || []);
    } catch {}
  }, []);

  useEffect(() => { loadPools(); }, [loadPools]);

  const handleAddPool = async () => {
    if (!form.name || !form.total_amount) return;
    setSaving(true);
    try {
      await incentiveAPI.createPool({...form, total_amount: parseFloat(form.total_amount)});
      toast.success('Incentive pool created');
      setForm({ name: '', total_amount: '', distribution_type: 'hours', period_start: weekAgo, period_end: today });
      setAddingPool(false);
      loadPools();
    } catch { toast.error('Failed to create pool'); }
    finally { setSaving(false); }
  };

  const handleDistribute = async (poolId) => {
    setDistributing(poolId);
    try {
      const r = await incentiveAPI.distributePool(poolId);
      toast.success(`Distributed to ${r.data.total} employee(s)`);
      setDistributions(prev => ({ ...prev, [poolId]: r.data.distributions }));
      setExpandedPool(poolId);
      loadPools();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to distribute'); }
    finally { setDistributing(null); }
  };

  const handleViewDistributions = async (poolId) => {
    if (expandedPool === poolId) { setExpandedPool(null); return; }
    if (!distributions[poolId]) {
      try {
        const r = await incentiveAPI.getDistributions(poolId);
        setDistributions(prev => ({ ...prev, [poolId]: r.data.distributions }));
      } catch {}
    }
    setExpandedPool(poolId);
  };

  const handleDeletePool = async (id) => {
    try { await incentiveAPI.deletePool(id); toast.success('Deleted'); loadPools(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="incentives-tab">

      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          Create bonus pools and distribute them automatically based on hours worked.
        </p>
        {user?.role === 'owner' && (
          <button onClick={() => setAddingPool(a => !a)} data-testid="add-incentive-pool-btn"
            className="flex items-center gap-1 text-xs font-medium text-orange-500 hover:text-orange-600">
            <Plus size={13} /> New Pool
          </button>
        )}
      </div>

      {/* Add Pool Form */}
      {addingPool && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">New Incentive Pool</p>
          <input placeholder="Pool name (e.g. Q1 Performance Bonus)"
            value={form.name}
            onChange={e => setForm(f => ({...f, name: e.target.value}))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            data-testid="pool-name" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-500">Total Amount ($)</label>
              <input type="number" placeholder="0.00"
                value={form.total_amount}
                onChange={e => setForm(f => ({...f, total_amount: e.target.value}))}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
                data-testid="pool-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Distribution</label>
              <select value={form.distribution_type}
                onChange={e => setForm(f => ({...f, distribution_type: e.target.value}))}
                className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                data-testid="pool-dist-type">
                <option value="hours">By hours worked</option>
                <option value="equal">Equal split</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Period Start</label>
              <input type="date" value={form.period_start}
                onChange={e => setForm(f => ({...f, period_start: e.target.value}))}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
                data-testid="pool-start" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Period End</label>
              <input type="date" value={form.period_end}
                onChange={e => setForm(f => ({...f, period_end: e.target.value}))}
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
                data-testid="pool-end" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAddingPool(false)}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={handleAddPool} disabled={saving} data-testid="save-pool-btn"
              className="flex-1 px-3 py-2 text-sm bg-slate-800 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2">
              <Zap size={14} /> {saving ? 'Saving…' : 'Create Pool'}
            </button>
          </div>
        </div>
      )}

      {/* Pool List */}
      {pools.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="pools-empty-state">
          <Zap size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No incentive pools yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pools.map(pool => (
            <div key={pool.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`pool-row-${pool.id}`}>
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800">{pool.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      pool.status === 'distributed'
                        ? 'bg-teal-50 text-teal-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>{pool.status}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {fmt(pool.total_amount)} · {pool.distribution_type} · {pool.period_start} → {pool.period_end}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {pool.status === 'distributed' && (
                    <button onClick={() => handleViewDistributions(pool.id)}
                      data-testid={`view-distributions-${pool.id}`}
                      className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200">
                      {expandedPool === pool.id ? <ChevronDown size={13}/> : <ChevronRight size={13}/>} View
                    </button>
                  )}
                  {pool.status === 'pending' && user?.role === 'owner' && (
                    <button onClick={() => handleDistribute(pool.id)}
                      disabled={distributing === pool.id}
                      data-testid={`distribute-pool-${pool.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                      <Zap size={13} /> {distributing === pool.id ? 'Distributing…' : 'Distribute'}
                    </button>
                  )}
                  {pool.status === 'pending' && user?.role === 'owner' && (
                    <button onClick={() => handleDeletePool(pool.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Distribution breakdown */}
              {expandedPool === pool.id && distributions[pool.id]?.length > 0 && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                  {distributions[pool.id].map(d => (
                    <div key={d.id} className="flex items-center justify-between px-5 py-2 bg-slate-50/50">
                      <div>
                        <p className="text-xs font-medium text-slate-700">{d.employee_name}</p>
                        {d.hours > 0 && <p className="text-xs text-slate-400">{d.hours}h worked</p>}
                      </div>
                      <p className="text-sm font-semibold text-teal-600">{fmt(d.amount)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
